import React, { useEffect, useState } from "react";
import { Button, Menu, MenuItem, Toggle } from "@showitinc/showit-wc";
import cx from "classnames";

import "./SplitMergeWrapper.css";

import { LAYOUT_DESKTOP, LAYOUT_MOBILE, LAYOUTS } from "../../constants/DesignConstants.mjs";
import UIActions from "@/actions/UIActions";

const handleToggle = (e) => {
  const newIndex = e?.detail?.newIndex;
  if (![undefined, null].includes(newIndex) && ![undefined, null].includes(LAYOUTS[newIndex])) {
    UIActions.selectActiveLayout({ layout: LAYOUTS[newIndex], from: "panel" });
  }
};

const handleMenuToggle = (e, menuRef) => {
  const contextBtn = e?.detail?.target;
  if (contextBtn && menuRef) {
    menuRef.showPopover(contextBtn);
  }
};

/**
 * A wrapper component that provides split/merge functionality for mobile and desktop layouts.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped
 * @param {boolean} props.sync - Whether the layouts are synchronized
 * @param {LAYOUTS[number]} props.layout - Current active layout ('mobile' or 'desktop')
 * @param {Boolean=} props.mixed - is prop mixed value
 * @param {() => void} props.toggleSync - Callback function to toggle sync state
 * @param {boolean} props.canToggle - Whether the toggle functionality is enabled
 * @returns {React.ReactElement} A wrapper component with split/merge functionality
 */
const SplitMergeWrapper = ({
  children,
  sync,
  layout,
  toggleSync,
  onlyShowDesktop = false,
  visible = true,
  removeCustomization,
  canToggle = true,
  mixed,
}) => {
  const [ref, setRef] = useState();
  const [menuRef, setMenuRef] = useState();
  const selected = layout === LAYOUT_MOBILE ? 0 : layout === LAYOUT_DESKTOP ? 1 : undefined;
  const menuText = sync
    ? "Split Mobile & Desktop"
    : layout
    ? `Merge using ${layout[0].toUpperCase()}${layout.slice(1)}`
    : null;

  useEffect(() => {
    ref &&
      ref.addEventListener("si-context-click", (e) => {
        e.stopPropagation();
        handleMenuToggle(e, menuRef);
      });

    return () => {
      ref &&
        ref.removeEventListener("si-context-click", (e) => {
          e.stopPropagation();
          handleMenuToggle(e, menuRef);
        });
    };
  }, [ref, menuRef, toggleSync]);

  if (!visible) {
    return null;
  }

  const content =
    !sync && !mixed ? <div className="split-merge-sub-fields">{children}</div> : children;

  return (
    <div className={cx(["split-merge-wrapper", { sync }])} ref={setRef}>
      {!sync && !onlyShowDesktop && !mixed && (
        <Toggle
          controlled
          slot="toggle"
          selected={selected}
          onSIToggle={handleToggle}
          className="split-merge-toggle"
        >
          <Button
            className="split-button"
            variant="tab"
            iconPrefix="mobileDevice"
            iconSize="20"
            slot="0"
          />
          <Button
            className="split-button"
            variant="tab"
            iconPrefix="desktopDevice"
            iconSize="20"
            slot="1"
          />
        </Toggle>
      )}
      {content}
      {canToggle || removeCustomization ? (
        <Menu ref={setMenuRef} shift="left">
          {canToggle && <MenuItem onClick={toggleSync}>{menuText}</MenuItem>}
          {removeCustomization && (
            <MenuItem onClick={removeCustomization}>Remove Customization</MenuItem>
          )}
        </Menu>
      ) : null}
    </div>
  );
};

export default SplitMergeWrapper;
